import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import type { CustomerReferralRow } from '../../mapCustomerReferralReports';

const textCell = (value: string | number) => (
  <div className="whitespace-nowrap text-xs font-medium text-foreground-detail">{value}</div>
);

export const getColumns = (t: (key: string) => string): ColumnDef<CustomerReferralRow>[] => [
  {
    accessorKey: 'sn',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
    cell: ({ row, table }) => textCell(getSerialNumber(table, row.index)),
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: 'referrerName',
    accessorKey: 'referrerName',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.referrerName')} />,
    cell: ({ row }) => {
      const name = row.getValue<string>('referrerName');
      const nickname = row.original.referrerNickname;
      const hasName = name && name !== '-';
      const hasNickname = nickname && nickname !== '-' && nickname !== name;
      if (!hasName && !hasNickname) return textCell('-');
      const primary = hasName ? name : nickname;
      const secondary = hasName && hasNickname ? nickname : null;
      return (
        <div className="max-w-[15rem]">
          <Link
            className="block truncate whitespace-nowrap text-xs font-medium text-info underline"
            to="/dashboard/referral-reports/$reportType/$referrerId"
            search={{ breadcrumbReturnTo: window.location.href }}
            params={{ reportType: 'customer', referrerId: row.original.referrerId }}
          >
            {primary}
          </Link>
          {secondary && (
            <div className="truncate whitespace-nowrap text-xs font-medium text-foreground-subtle">{secondary}</div>
          )}
        </div>
      );
    },
  },
  {
    id: 'reservedBy',
    accessorKey: 'reservedBy',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.reservedBy')} />,
    cell: ({ row }) => {
      const name = row.getValue<string>('reservedBy');
      const nickname = row.original.reservedByNickname;
      const hasName = name && name !== '-';
      const hasNickname = nickname && nickname !== '-';
      if (!hasName && !hasNickname) return textCell('-');
      const primary = hasName ? name : '-';
      const secondary = hasNickname ? nickname : null;
      return (
        <div className="max-w-[15rem]">
          <div className="truncate whitespace-nowrap text-xs font-medium text-foreground-detail">{primary}</div>
          {secondary && (
            <div className="truncate whitespace-nowrap text-xs font-medium text-foreground-subtle">{secondary}</div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'reservationDateTime',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.page.tableColumn.reservationDateTime')} />
    ),
    cell: ({ row }) => textCell(formatDate(row.getValue<number>('reservationDateTime'), 'YYYY/MM/DD HH:mm:ss')),
  },
  {
    accessorKey: 'pointsEarned',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.pointsEarned')} />,
    cell: ({ row }) => {
      const points = row.getValue<number | null>('pointsEarned');
      const isUnavailable = points === null;
      return (
        <div
          className={`whitespace-nowrap text-xs font-medium ${isUnavailable ? 'text-foreground-detail' : 'text-success'}`}
        >
          {isUnavailable ? '-' : `+${formatAmount(points, ' ')}`}
        </div>
      );
    },
  },
  {
    accessorKey: 'stageDetails',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.stageDetails')} />,
    cell: ({ row }) => (
      <div className="whitespace-nowrap text-xs font-medium text-success">{row.getValue<string>('stageDetails')}</div>
    ),
  },
  {
    id: 'createdDate',
    accessorFn: (row) => row.receiptDateTime,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.page.tableColumn.receiptDateTime')} />
    ),
    cell: ({ row }) => {
      const value = row.getValue<number | null>('createdDate');
      return value === null ? textCell('-') : textCell(formatDate(value, 'YYYY/MM/DD HH:mm:ss'));
    },
    meta: { label: t('common.page.tableColumn.receiptDateTime') },
    enableColumnFilter: true,
  },
];
